// =============================================================================
// ForcePasswordModal — blocking password creation for passwordless accounts
// (password auth v1 follow-up, 2026-09-15, per Sid: hard-block, no skip)
// =============================================================================
// Shows whenever an authenticated user has hasPassword === false (strict:
// undefined means the flag isn't authoritative yet — fail open, never lock
// someone out on stale data). AppLayout's useCurrentUser() refreshes the
// flag from /auth/me on every app load, so the gate triggers on any visit,
// not just fresh logins — required because sessions are 30-day rolling.
//
// Uses the change-password first-time-set path (no currentPassword: the
// OTP-authenticated session is the proof). The current session survives;
// other devices are revoked server-side.
//
// Deliberately NOT dismissible: no X, no backdrop close, no ESC. The only
// out is "Sign out" (wrong account / shared computer), which does not
// bypass the requirement — the modal returns on next login.
// =============================================================================

import { useState } from 'react';
import { KeyRound, LogOut } from 'lucide-react';
import { useAuthStore } from '@/stores/auth';
import { useChangePassword, useLogout } from '@/hooks/useAuth';
import { PasswordFields } from '@/components/auth/PasswordFields';

export function ForcePasswordModal() {
  const { user, _diag, updateUser } = useAuthStore();
  const changePassword = useChangePassword();
  const { mutate: logout, isPending: isLoggingOut } = useLogout();

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [formError, setFormError] = useState('');

  // Strict === false: stale persisted stores (pre-v1) lack the field, and an
  // in-flight /auth/me must never flash a lockout. Diag replay sessions
  // impersonate real passwordless users — never block them.
  if (!user || user.hasPassword !== false || _diag?.active) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');

    if (newPassword.length < 12) {
      setFormError('Password must be at least 12 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setFormError('Passwords do not match');
      return;
    }

    changePassword.mutate(
      { newPassword },
      {
        onSuccess: () => {
          // Unmount instantly; the ['auth','me'] invalidation (inside the
          // hook) confirms from the server shortly after.
          updateUser({ hasPassword: true });
        },
        onError: (err) => {
          const apiErr = err as { message?: string };
          setFormError(apiErr.message || 'Failed to save password. Please try again.');
        },
      }
    );
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-8 my-8">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-[#17DDAB]/15 to-[#009FE4]/15 flex items-center justify-center">
          <KeyRound className="w-8 h-8 text-[#009FE4]" />
        </div>

        <h2 className="text-xl font-semibold text-gray-900 mb-2 text-center">
          Create your password
        </h2>

        <p className="text-gray-600 mb-6 text-sm text-center">
          Transakt accounts now use a password to sign in. Create one for{' '}
          <span className="font-medium text-gray-800">{user.email || 'your account'}</span> to
          continue — you&apos;ll still be able to sign in with an emailed code any time.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <PasswordFields
            newPassword={newPassword}
            confirmPassword={confirmPassword}
            onNewPasswordChange={setNewPassword}
            onConfirmPasswordChange={setConfirmPassword}
            error={formError}
          />

          {formError && <p className="text-sm text-red-600">{formError}</p>}

          <button
            type="submit"
            disabled={changePassword.isPending}
            className="w-full px-6 py-3 bg-gradient-to-r from-[#17DDAB] to-[#009FE4] text-white font-medium rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {changePassword.isPending ? 'Saving...' : 'Save password & continue'}
          </button>
        </form>

        <button
          type="button"
          onClick={() => logout()}
          disabled={isLoggingOut}
          className="mt-4 w-full flex items-center justify-center gap-1.5 text-sm text-gray-400 hover:text-gray-600 disabled:text-gray-300"
        >
          <LogOut className="w-3.5 h-3.5" />
          Not you? Sign out
        </button>
      </div>
    </div>
  );
}
